<template>
  <div class="log">
    <button type="button" class="doc-ass-btn" @click="dialog = true">
      Documents
    </button>
    <v-dialog
      v-model="dialog"
      scrollable
      content-class="all-documents-dialog"
    >
      <v-card>
        <v-card-title>
          <span class="headline">All Documents</span>
          <v-spacer />
          <div class="all-documents-hint">
            {{
              selecting
                ? "Pick them in merge order"
                : "Select to make merged PDF"
            }}
          </div>
          <v-btn
            small
            flat
            class="all-documents-select"
            @click="toggleSelecting"
            >{{ selecting ? "Cancel" : "Select" }}</v-btn
          >
          <div @click="dialog = false">
            <v-icon class="pointer">close</v-icon>
          </div>
        </v-card-title>
        <v-card-text style="height: 100%">
          <div
            v-for="group in documentGroups"
            :key="group.name"
            class="all-documents-group"
          >
            <div class="all-documents-group__title">{{ group.name }}</div>
            <v-layout wrap>
              <v-flex
                xs4
                v-for="document in group.documents"
                :key="document.key"
              >
                <div
                  class="all-documents-tile"
                  :class="{
                    'all-documents-tile--selectable': selecting,
                    'all-documents-tile--selected': !!selectionIndex(document),
                  }"
                >
                  <div class="all-documents-tile__thumb">
                    <a
                      :href="document.url"
                      class="document-link"
                      target="_blank"
                      @click="openDocument(document, $event)"
                    >
                      <img
                        v-if="document.isPdf"
                        src="../../../assets/pfd.png"
                      />
                      <img
                        v-else
                        src="../../../assets/docimg.webp"
                        height="72"
                        width="75"
                      />
                    </a>
                    <span
                      v-if="selectionIndex(document)"
                      class="all-documents-tile__order"
                      >{{ selectionIndex(document) }}</span
                    >
                  </div>
                  <div
                    class="all-documents-tile__label"
                    @click="openDocument(document, $event)"
                  >
                    <span
                      v-if="selecting"
                      class="all-documents-tile__check"
                      :class="{
                        'all-documents-tile__check--on':
                          !!selectionIndex(document),
                      }"
                    >
                      <v-icon v-if="selectionIndex(document)">check</v-icon>
                    </span>
                    <span class="heading all-documents-tile__name">{{
                      document.label
                    }}</span>
                  </div>
                </div>
              </v-flex>
            </v-layout>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            v-if="selectedDocuments.length > 1"
            class="oms-dialog-action--primary all-documents-download"
            :loading="merging"
            :disabled="merging"
            @click="createPdf"
          >
            <v-icon left small>file_download</v-icon>
            Download PDF
          </v-btn>
          <v-btn color="blue darken-1" flat @click="dialog = false"
            >Close</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { StorageKeys } from "../../../constants/constants";
import downloadMixin from "@/mixins/downloadMixin";

export default {
  mixins: [downloadMixin],
  props: {
    detail: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      dialog: false,
      documents: [],
      selecting: false,
      merging: false,
      // Selection order is the merge order, so this holds keys in the order
      // they were picked - not the order the documents are listed in.
      selectedKeys: [],
    };
  },
  watch: {
    dialog(isOpen) {
      if (isOpen) {
        this.selecting = false;
        this.selectedKeys = [];
        this.getDocuments();
      }
    },
  },
  computed: {
    // Documents sit under their API group - System Generated, Shipment
    // Documents, Additional Charge Documents - in the order the API sends them.
    documentGroups() {
      return this.documents.reduce((groups, document) => {
        const group = groups.find((row) => row.name === document.group);
        if (group) {
          group.documents.push(document);
        } else {
          groups.push({ name: document.group, documents: [document] });
        }
        return groups;
      }, []);
    },
    selectedDocuments() {
      return this.selectedKeys
        .map((key) => this.documents.find((document) => document.key === key))
        .filter(Boolean);
    },
    // What the merge endpoint will be sent, in the order the user picked. The
    // API's own key and source ride along so it can resolve each item - the
    // waybill in particular is a page to render, not a file to fetch.
    mergePayload() {
      return {
        assignmentId: this.detail.assignmentId,
        documents: this.selectedDocuments.map((document) => ({
          key: document.key,
          source: document.source,
          label: document.label,
          url: document.url,
        })),
      };
    },
  },
  methods: {
    toggleSelecting() {
      this.selecting = !this.selecting;
      this.selectedKeys = [];
    },
    // 1-based position in the merge order, 0 when the document is not selected.
    selectionIndex(document) {
      return this.selectedKeys.indexOf(document.key) + 1;
    },
    // While selecting, a tile picks and unpicks instead of opening the file.
    openDocument(document, event) {
      if (!this.selecting) {
        return;
      }
      event.preventDefault();
      const position = this.selectedKeys.indexOf(document.key);
      if (position === -1) {
        this.selectedKeys.push(document.key);
      } else {
        this.selectedKeys.splice(position, 1);
      }
    },
    // The endpoint builds the bundle and returns where it was written, so the
    // file is pulled from that URL rather than streamed through the response.
    createPdf() {
      this.merging = true;
      delete this.axios.defaults.headers.common["token"];
      const token = localStorage.getItem(StorageKeys.SessionKey);
      const config = { headers: { authorization: `bearer ${token}` } };
      this.axios
        .post(
          `${this.constants.apiUrl}/dmsAdmin/mergeShipmentDocumentsPdf`,
          this.mergePayload,
          config
        )
        .then(
          (response) => {
            const { url, fileName } = response.data.data;
            this.forceDownload(url, fileName);
            this.merging = false;
            this.selecting = false;
            this.selectedKeys = [];
          },
          () => {
            this.merging = false;
          }
        );
    },
    // Signed URLs carry a query string, so the extension is read off the path.
    isPdf(url) {
      const path = url.split(/[?#]/)[0];
      return path.substr(path.lastIndexOf(".") + 1).toLowerCase() === "pdf";
    },
    getDocuments() {
      delete this.axios.defaults.headers.common["token"];
      const token = localStorage.getItem(StorageKeys.SessionKey);
      const config = { headers: { authorization: `bearer ${token}` } };
      const body = { assignmentId: this.detail.assignmentId };
      this.axios
        .post(
          `${this.constants.apiUrl}/dmsAdmin/getAllShipmentDocuments`,
          body,
          config
        )
        .then(
          (response) => {
            this.documents = this.toDocuments(response.data.data);
          },
          () => {
            this.documents = [];
          }
        );
    },
    // The waybill is a printable page rather than an uploaded file, which is why
    // the API sends its entry with no files for the client to resolve itself.
    waybillHref() {
      return this.$router.resolve({
        path: `/assignments/${this.detail.assignmentId}/${this.detail.workingCountry}/waybill`,
      }).href;
    },
    // The API groups files under one entry per document - Sale Invoice, each
    // shipment type, each additional charge - and an entry can hold several
    // files. The grid shows one tile per file, numbered when there are several.
    toDocuments(data) {
      const entries = (data && data.documents) || [];
      return entries.reduce((tiles, entry) => {
        if (entry.source === "waybill") {
          tiles.push({
            key: entry.key,
            group: "Waybill",
            label: entry.label,
            source: entry.source,
            url: this.waybillHref(),
            isPdf: true,
          });
          return tiles;
        }
        const files = entry.files || [];
        files.forEach((file, index) => {
          if (!file.url) {
            return;
          }
          tiles.push({
            key: `${entry.key}:${index}`,
            group:
              entry.source === "salesInvoice"
                ? "Sales Invoice"
                : entry.group || "Documents",
            label:
              files.length > 1
                ? `${entry.label} (${index + 1})`
                : entry.label,
            source: entry.source,
            url: file.url,
            isPdf: this.isPdf(file.url),
          });
        });
        return tiles;
      }, []);
    },
  },
};
</script>

<!-- Unscoped: the dialog is teleported out of this component's root. Dialogs are
     shrink-to-fit globally, which leaves short labels in a narrow card, so this
     one takes the full width the global cap allows. -->
<style>
.application .v-dialog.all-documents-dialog {
  inline-size: min(44rem, calc(100vw - 2rem));
  width: min(44rem, calc(100vw - 2rem));
}

.application .v-dialog.all-documents-dialog .all-documents-group + .all-documents-group {
  margin-top: 1.25rem;
}

/* Same label treatment the expanded row uses for its column headings. */
.application .v-dialog.all-documents-dialog .all-documents-group__title {
  color: #64748b;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  margin-bottom: 0.75rem;
  text-transform: uppercase;
}

.application .v-dialog.all-documents-dialog .all-documents-tile {
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0 0.5rem 1rem;
  text-align: center;
}

.application .v-dialog.all-documents-dialog .all-documents-tile__name {
  font-size: 0.8125rem !important;
  line-height: 1.3 !important;
  overflow-wrap: anywhere;
}

.application .v-dialog.all-documents-dialog .v-card__title {
  gap: 0.5rem;
}

/* A div, not a span: the dialog styles every span in a title as the heading. */
.application .v-dialog.all-documents-dialog .all-documents-hint {
  background: rgba(234, 122, 28, 0.09);
  border-radius: 999px;
  color: #b45309;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  padding: 0.1875rem 0.5625rem;
  white-space: nowrap;
}

.application .v-dialog.all-documents-dialog .all-documents-select.v-btn {
  border: 0.0625rem solid rgba(234, 122, 28, 0.45) !important;
  border-radius: 0.5rem !important;
  color: #ea7a1c !important;
  font-size: 0.6875rem !important;
  font-weight: 800 !important;
  height: 1.75rem !important;
  margin: 0 !important;
  min-width: 0 !important;
  padding: 0 0.625rem !important;
}

.application .v-dialog.all-documents-dialog .all-documents-select.v-btn:hover {
  background: rgba(234, 122, 28, 0.08) !important;
}

/* The primary-action class colours the button, so the icon follows its text. */
.application .v-dialog.all-documents-dialog .all-documents-download .v-icon {
  color: inherit !important;
}

.application .v-dialog.all-documents-dialog .all-documents-tile__thumb {
  display: inline-flex;
  position: relative;
}

.application .v-dialog.all-documents-dialog .all-documents-tile__label {
  align-items: center;
  display: flex;
  gap: 0.375rem;
  justify-content: center;
  max-width: 100%;
}

/* Reads with the name rather than over the thumbnail. */
.application .v-dialog.all-documents-dialog .all-documents-tile__check {
  align-items: center;
  background: #ffffff;
  border: 0.125rem solid #cbd5e1;
  border-radius: 0.25rem;
  display: flex;
  flex: 0 0 auto;
  height: 1.125rem;
  justify-content: center;
  width: 1.125rem;
}

.application .v-dialog.all-documents-dialog .all-documents-tile__check--on {
  background: #2563eb;
  border-color: #2563eb;
}

.application .v-dialog.all-documents-dialog .all-documents-tile__check .v-icon {
  color: #ffffff !important;
  font-size: 0.8125rem !important;
}

/* While selecting, the name and its box pick the document too. */
.application
  .v-dialog.all-documents-dialog
  .all-documents-tile--selectable
  .all-documents-tile__label {
  cursor: pointer;
}

/* The merge position, so the order picked is visible on the tiles. */
.application .v-dialog.all-documents-dialog .all-documents-tile__order {
  align-items: center;
  background: #2563eb;
  border: 0.125rem solid #ffffff;
  border-radius: 50%;
  color: #ffffff;
  display: flex;
  font-size: 0.6875rem;
  font-weight: 800;
  height: 1.375rem;
  justify-content: center;
  position: absolute;
  right: -0.5rem;
  top: -0.5rem;
  width: 1.375rem;
}

.application .v-dialog.all-documents-dialog .all-documents-tile--selected img {
  border-color: #2563eb !important;
}
</style>
